<template>
  <svg
    ref="root"
    class="kds-round-table"
    :class="{ 'kds-round-table--paused': paused }"
    :width="size"
    :height="size"
    :viewBox="`0 0 ${size} ${size}`"
    role="img"
    aria-label="Six personas around a debate table, one currently speaking."
  >
    <!-- Table outline -->
    <circle
      :cx="size / 2"
      :cy="size / 2"
      :r="size / 2 - 40"
      fill="none"
      stroke="var(--mist)"
      stroke-width="1"
    />
    <!-- Seats -->
    <g class="kds-round-table__seats">
      <circle
        v-for="(seat, idx) in seats"
        :key="idx"
        :cx="seat.x"
        :cy="seat.y"
        :r="seat.live ? 8 : 6"
        :fill="seat.live ? 'var(--signal)' : 'var(--ink)'"
        :class="{ 'kds-pulse': seat.live && !paused }"
        :stroke="seat.live ? 'var(--signal-press)' : 'none'"
        :stroke-width="seat.live ? 2 : 0"
      />
    </g>
    <!-- Trace -->
    <path
      ref="traceEl"
      :d="tracePath"
      class="kds-round-table__trace"
      :class="{ 'kds-trace': !paused }"
      fill="none"
      stroke="var(--ink)"
      stroke-width="1.5"
      stroke-linecap="round"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

interface Props {
  size?: 200 | 480;
  /** 0-based index of the speaking seat. */
  speakingIndex?: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: 480,
  speakingIndex: 0
});

const emit = defineEmits<{
  'speaker-change': [payload: { index: number; turnId: number }];
}>();

// Monotone counter incremented on each handled iteration, used as TransitionGroup key.
let turnIdCounter = 0;

const root = ref<SVGSVGElement | null>(null);
const traceEl = ref<SVGPathElement | null>(null);
const paused = ref(false);
const prefersReducedMotion = ref(false);
const currentSpeakerIndex = ref<number>(props.speakingIndex);

watch(
  () => props.speakingIndex,
  (next) => {
    currentSpeakerIndex.value = next;
  }
);

interface Seat {
  x: number;
  y: number;
  live: boolean;
}

const seats = computed<Seat[]>(() => {
  const count = 6;
  const cx = props.size / 2;
  const cy = props.size / 2;
  const radius = props.size / 2 - 60;
  const result: Seat[] = [];
  for (let i = 0; i < count; i += 1) {
    const angle = (Math.PI * 2 * i) / count - Math.PI / 2;
    result.push({
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
      live: i === currentSpeakerIndex.value
    });
  }
  return result;
});

const tracePath = computed(() => {
  const list = seats.value;
  if (list.length < 2) return '';
  const speaking = list[currentSpeakerIndex.value] ?? list[0]!;
  const target = list[(currentSpeakerIndex.value + 2) % list.length]!;
  // Quadratic Bezier control point placed at the table center (per design).
  const mx = props.size / 2;
  const my = props.size / 2;
  return `M ${speaking.x} ${speaking.y} Q ${mx} ${my} ${target.x} ${target.y}`;
});

const pickNextSpeaker = (): number => {
  const count = 6;
  const maxRetries = 10;
  for (let i = 0; i < maxRetries; i += 1) {
    const candidate = Math.floor(Math.random() * count);
    if (candidate !== currentSpeakerIndex.value) return candidate;
  }
  return (currentSpeakerIndex.value + 1) % count;
};

const handleTraceIteration = (): void => {
  if (paused.value || prefersReducedMotion.value) return;
  currentSpeakerIndex.value = pickNextSpeaker();
  emit('speaker-change', { index: currentSpeakerIndex.value, turnId: ++turnIdCounter });
};

let observer: IntersectionObserver | null = null;
let mediaQuery: MediaQueryList | null = null;

const handleMediaChange = (event: MediaQueryListEvent): void => {
  prefersReducedMotion.value = event.matches;
};

onMounted(() => {
  if (typeof window === 'undefined') return;

  if (typeof window.matchMedia === 'function') {
    mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.value = mediaQuery.matches;
    mediaQuery.addEventListener('change', handleMediaChange);
  }

  traceEl.value?.addEventListener('animationiteration', handleTraceIteration);

  if (typeof IntersectionObserver === 'undefined' || !root.value) return;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        paused.value = !entry.isIntersecting || entry.intersectionRatio < 0.5;
      }
    },
    { threshold: [0.0, 0.5, 1.0] }
  );
  observer.observe(root.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  traceEl.value?.removeEventListener('animationiteration', handleTraceIteration);
  mediaQuery?.removeEventListener('change', handleMediaChange);
  mediaQuery = null;
});

// Expose paused state so parent containers (e.g. LandingPage) can mirror it visually.
defineExpose({ paused });
</script>

<style scoped lang="scss">
.kds-round-table {
  display: block;
  max-width: 100%;
  height: auto;

  &__trace {
    transition: opacity var(--d-base) var(--ease-out);
  }

  &--paused .kds-round-table__trace {
    opacity: 0.4;
    animation: none !important;
  }

  &--paused .kds-pulse-dot {
    animation: none !important;
  }
}
</style>
